import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, DownloadIcon, SearchIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { Reveal } from '../components/ui/Reveal';
import { EmptyState } from '../components/ui/EmptyState';
import { CtaBand } from '../components/ui/CtaBand';
import { policies, policyFilters } from '../data/policies';
export function Agenda() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');
  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    return policies.filter(policy => {
      const matchesFilter = filter === 'All' || policy.category === filter;
      const matchesQuery = !term || policy.title.toLowerCase().includes(term) || policy.summary.toLowerCase().includes(term);
      return matchesFilter && matchesQuery;
    });
  }, [query, filter]);
  const reset = () => {
    setQuery('');
    setFilter('All');
  };
  return <>
      <PageHero eyebrow="Presidential agenda" title="Our plan for Nigeria, in full." intro="Nine policy areas. Each one states the challenge, the objective, the plan, what happens in the first hundred days and how progress will be measured." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Agenda'
    }]} data-id="element-621" data-mp="18rxnnxzgkfk" />

      <section className="bg-sand-50 py-12 lg:py-16" aria-label="Policy search and filters" data-id="element-622" data-mp="jxymztta8aac">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-623" data-mp="k0b09to3hwh2">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-624" data-mp="ovi0gri5oj18">
            <div className="relative w-full lg:max-w-sm" data-id="element-625" data-mp="sz66v6v1x8kw">
              <label htmlFor="policy-search" className="sr-only" data-id="element-626" data-mp="xy9dgvqojmuz">
                Search policies
              </label>
              <SearchIcon aria-hidden className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-500" data-id="element-627" data-mp="2ur2tpcmo64uz" />
              <input id="policy-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search the agenda" className="w-full rounded-lg border border-sand-300 bg-white py-3 pl-11 pr-4 text-[15px] text-ink placeholder:text-slate-500/70 focus:border-green-600 focus:outline-none" data-id="element-628" data-mp="6tuh1vsn53so" />
            </div>

            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category" data-id="element-629" data-mp="pr5ct6f2df3d">
              {policyFilters.map(category => {
              const active = filter === category;
              return <button key={category} type="button" aria-pressed={active} onClick={() => setFilter(category)} className={`rounded-lg border px-4 py-2 text-[14px] font-medium transition-colors duration-150 ease-emphasis ${active ? 'border-green-600 bg-green-600 text-white' : 'border-sand-300 bg-white text-slate-600 hover:border-slate-500/50 hover:text-ink'}`} data-id="element-630" data-mp="ukgj96fyrtur">
                    {category}
                  </button>;
            })}
            </div>
          </div>

          <p className="mt-6 text-[14px] text-slate-500" role="status" data-id="element-631" data-mp="14ggq3pmsv40f">
            {results.length} {results.length === 1 ? 'policy' : 'policies'}
            {filter !== 'All' ? ` in ${filter}` : ''}
          </p>

          {results.length === 0 ? <div className="mt-6" data-id="element-632" data-mp="uju7z8luyr23">
              <EmptyState title="No policy matches those filters" description="Try a different category, or search for a keyword such as jobs, power, security or schools." actionLabel="Clear filters" onAction={reset} data-id="element-633" data-mp="zgugdq66k5wd" />
            </div> : <ul className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-634" data-mp="ieol16n837v7">
              {results.map((policy, index) => <li key={policy.slug} data-id="element-635" data-mp="dh5604ipar27" data-mpi={index}>
                  <Reveal delay={Math.min(index, 6) * 0.03} className="h-full" data-id="element-636" data-mp="8pn7dn45h2ke">
                    <Link to={`/agenda/${policy.slug}`} className="group flex h-full flex-col rounded-card border border-sand-200 bg-white p-6 transition-[border-color,box-shadow] duration-200 ease-emphasis hover:border-green-200 hover:shadow-card" data-id="element-637" data-mp="8nk6g81glp0w">
                      <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-green-700" data-id="element-638" data-mp="3lwggww4hcbyo">
                        {policy.category}
                      </span>
                      <span className="mt-2 font-display text-[21px] leading-snug text-ink" data-id="element-639" data-mp="3gzggp1uba3fh">
                        {policy.title}
                      </span>
                      <span className="mt-2.5 text-[15px] leading-relaxed text-slate-600" data-id="element-640" data-mp="1f71w1v0dukvd">
                        {policy.summary}
                      </span>
                      <span className="mt-auto inline-flex items-center gap-2 pt-6 text-[14px] font-semibold text-green-700" data-id="element-641" data-mp="1rocr8gxp8wzl">
                        Explore policy
                        <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-642" data-mp="1wg32qq8dhqsl" />
                      </span>
                    </Link>
                  </Reveal>
                </li>)}
            </ul>}
        </div>
      </section>

      <section className="bg-white py-14 lg:py-20" aria-label="Manifesto and related plans" data-id="element-643" data-mp="1dnigvsr3clya">
        <div className="mx-auto grid max-w-content gap-6 px-4 sm:px-6 lg:grid-cols-3 lg:px-10" data-id="element-644" data-mp="1ip7t3bq1v5ny">
          <div className="rounded-media border border-sand-200 bg-sand-50 p-7 lg:col-span-2" data-id="element-645" data-mp="2p3gxqisigca2">
            <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-green-700" data-id="element-646" data-mp="2u1iegx2grzid">
              Full document
            </p>
            <h2 className="mt-2 font-display text-[26px] leading-snug text-ink" data-id="element-647" data-mp="1zfuyyyoade8q">
              “A Nigeria That Works” — the complete manifesto
            </h2>
            <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-slate-600" data-id="element-648" data-mp="2eclo8f48x9km">
              Ninety-six pages including the cost model, delivery framework and the full set of
              published targets. Free to reproduce and distribute.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4" data-id="element-649" data-mp="1gpvaacwcy15u">
              <Link to="/manifesto" className="inline-flex h-11 items-center gap-2 rounded-lg bg-green-600 px-5 text-[15px] font-semibold text-white transition-colors duration-150 ease-emphasis hover:bg-green-700" data-id="element-650" data-mp="1gniofonhb6ni">
                <DownloadIcon aria-hidden className="h-[18px] w-[18px]" data-id="element-651" data-mp="12wdsoxn8eyig" />
                Manifesto centre
              </Link>
              <span className="text-[13px] text-slate-500" data-id="element-652" data-mp="xx17sifx81ii">PDF · 8.2 MB · English</span>
            </div>
          </div>

          <div className="flex flex-col gap-4" data-id="element-653" data-mp="3ecnspv1me4zt">
            <Link to="/first-100-days" className="group flex flex-1 flex-col justify-between rounded-card border border-sand-200 p-6 transition-colors duration-200 ease-emphasis hover:border-green-200 hover:bg-sand-50" data-id="element-654" data-mp="3eeiefbkpu14y">
              <span className="font-display text-[19px] text-ink" data-id="element-655" data-mp="3sc71mq299kd6">The First 100 Days</span>
              <span className="mt-2 inline-flex items-center gap-2 text-[14px] font-semibold text-green-700" data-id="element-656" data-mp="165n27j308ms">
                What happens immediately
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-657" data-mp="3jbqxrfhlob7s" />
              </span>
            </Link>
            <Link to="/nigeria" className="group flex flex-1 flex-col justify-between rounded-card border border-sand-200 p-6 transition-colors duration-200 ease-emphasis hover:border-green-200 hover:bg-sand-50" data-id="element-658" data-mp="2bygsv606y7x7">
              <span className="font-display text-[19px] text-ink" data-id="element-659" data-mp="2ym5i08vlgv4j">Regional Agenda</span>
              <span className="mt-2 inline-flex items-center gap-2 text-[14px] font-semibold text-green-700" data-id="element-660" data-mp="2umd7vrqjygx2">
                What it means where you live
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-661" data-mp="3mci0gdogua9d" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <CtaBand title="Found the policy that matters to you?" description="Join the campaign updates list and we will send the detail as each plan is published." primary={{
      label: 'Join the movement',
      to: '/join'
    }} secondary={{
      label: 'Ask a question',
      to: '/contact'
    }} data-id="element-662" data-mp="3did1f6z6hsir" />
    </>;
}